import { TeethRule, TeethUnitType, UnitTypeRuleWithSpecs, UnitTypesAdditionalRules } from '@shared/models/rx-rules-json-interface';
import { VersionsService } from '../versions.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { HostPlatformService } from '../host-platform.service';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { ApplicationModes, SoftwareOptions } from '@shared/models/enums/enums';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { LimitedFeatures } from '@shared/models/limited-features';
import { ProcedureUnitTypeRule } from '@core/procedure-helpers/models/unit-type-rules-config';
import { ProcedureMapCheckedValue } from '@core/procedure-helpers/models/procedure-map-composites';
import { pmCompositeMatcher } from '@shared/utils/procedure-map-composites';
import { Maybe } from '@/types/globals';

export class ProcedureRulesParser<TUnitTypes extends number> {
	private readonly defaultProcedureRules: ProcedureUnitTypeRule[];

	constructor(
		private procedureRules: ProcedureUnitTypeRule[],
		private versionService: VersionsService,
		private hostPlatformService: HostPlatformService,
		private shellQuery: ShellQuery
	) {
		this.defaultProcedureRules = procedureRules;
	}

	getDefaultRules(): ProcedureUnitTypeRule[] {
		return this.defaultProcedureRules;
	}

	setRules(rules: ProcedureUnitTypeRule[]): this {
		this.procedureRules = rules;

		return this;
	}

	getUnitTypeIdsByProcedure(pmToCheck: Maybe<ProcedureMapCheckedValue>, rxConfiguration: Maybe<RxConfiguration>): number[] {
		const teethRules = this.getAllTeethRules(pmToCheck);

		const unitTypeIds: number[] = teethRules.flatMap(
			teethRule => teethRule.UnitAndMaterialRules.UnitTypesAdditionalRules?.UnitTypesAddedOutput ?? []
		);

		// add all unit type ids from teeth rules
		return teethRules.reduce((accumulator: number[], teethRule: TeethRule) => {
			return this.filterFeatureRules(accumulator, teethRule, rxConfiguration);
		}, unitTypeIds);
	}

	getUnitTypeIdsByProcedureMapAndToothId(
		pmToCheck: Maybe<ProcedureMapCheckedValue>,
		toothId: number,
		currentUnitTypeOnTooth: Maybe<number>,
		rxConfiguration?: RxConfiguration
	): number[] {
		const teethRules = this.getTeethRules(pmToCheck, toothId);

		const additionalUnitTypeIds: number[] =
			currentUnitTypeOnTooth !== undefined && currentUnitTypeOnTooth !== null
				? this.getAdditionalUnitTypeIds({ teethRules, toothId, currentUnitTypeOnTooth })
				: [];

		const unitTypeIds: number[] = teethRules?.reduce((accumulator: number[], teethRule: TeethRule) => {
			return this.filterFeatureRules(accumulator, teethRule, rxConfiguration);
		}, additionalUnitTypeIds);

		return [...new Set(unitTypeIds ?? [])];
	}

	getTeethUnitTypeRule(pmToCheck: ProcedureMapCheckedValue): TeethUnitType[] | null {
		const procedureRule = this.procedureRules?.find((pr: ProcedureUnitTypeRule) =>
			pr.ProcedureMaps.some(p => pmCompositeMatcher(p, pmToCheck))
		);

		if (!procedureRule?.TeethRules?.length) {
			return null;
		}

		const getUnitTypeRules = (accum: TeethUnitType[], curr: TeethRule) => {
			const unitTypeIds: number[] = [];

			(curr.UnitAndMaterialRules.UnitTypeRules as UnitTypeRuleWithSpecs[]).forEach(unitTypeRule => {
				unitTypeIds.push(unitTypeRule.Id);
			});
			curr.UnitAndMaterialRules?.UnitTypesAdditionalRules?.UnitTypesAddedOutput.forEach((id: number) => {
				unitTypeIds.push(id);
			});
			accum.push({
				TeethIds: curr.Ids,
				UnitTypeIds: unitTypeIds
			});

			return accum;
		};

		return procedureRule.TeethRules.reduce(getUnitTypeRules, []);
	}

	getAllTeethRules(pmToCheck: Maybe<ProcedureMapCheckedValue>): TeethRule[] {
		const procedureRule = this.getProcedureRule(pmToCheck);

		return procedureRule?.TeethRules ?? [];
	}

	private filterFeatureRules(accumulator: number[], teethRule: TeethRule, rxConfiguration: Maybe<RxConfiguration>): number[] {
		(teethRule.UnitAndMaterialRules.UnitTypeRules as UnitTypeRuleWithSpecs[]).forEach(unitTypeRule => {
			if (unitTypeRule.RestrictionUnitTypes) {
				if (this.isRestrictedUnitTypeAvailability(unitTypeRule, rxConfiguration)) {
					accumulator.push(unitTypeRule.Id);
				}
			} else {
				accumulator.push(unitTypeRule.Id);
			}
		});

		return accumulator;
	}

	private getTeethRules(pmToCheck: Maybe<ProcedureMapCheckedValue>, toothId: number): TeethRule[] {
		return this.getAllTeethRules(pmToCheck).filter((teethRule: TeethRule) => teethRule.Ids.includes(toothId)) ?? [];
	}

	private getProcedureRule(pmToCheck: Maybe<ProcedureMapCheckedValue>): Maybe<ProcedureUnitTypeRule> {
		return (
			// find _first_ matching rule
			this.procedureRules?.find((procedureRule: ProcedureUnitTypeRule) =>
				procedureRule.ProcedureMaps.some(p => pmCompositeMatcher(p, pmToCheck))
			) ?? null
		);
	}

	private getAdditionalUnitTypeIds({
		teethRules,
		toothId,
		currentUnitTypeOnTooth
	}: {
		teethRules: TeethRule[];
		toothId: number;
		currentUnitTypeOnTooth: number;
	}): number[] {
		const getAdditionalUnitTypeIdsFromTeethRules = (accumulator: number[], teethRule: TeethRule) => {
			if (teethRule.Ids.includes(toothId) && teethRule.UnitAndMaterialRules.UnitTypesAdditionalRules) {
				const additionalRules: UnitTypesAdditionalRules = teethRule.UnitAndMaterialRules.UnitTypesAdditionalRules;

				if (additionalRules.UnitTypesInput.includes(currentUnitTypeOnTooth)) {
					accumulator.push(...additionalRules.UnitTypesAddedOutput);
				}
			}

			return accumulator;
		};

		return teethRules?.reduce(getAdditionalUnitTypeIdsFromTeethRules, []);
	}

	private doesFeatureHaveSWO(softwareOptions: SoftwareOptions[], rxConfiguration: Maybe<RxConfiguration>): boolean {
		return (
			!!rxConfiguration &&
			SoftwareOptionsService.checkSoftwareOptions(rxConfiguration.CompanyConfiguration.SoftwareOptionsForCompany, softwareOptions)
		);
	}

	private isfeatureAvailable(limitedFeatures: LimitedFeatures): boolean {
		if (this.hostPlatformService.isScanner) {
			return this.versionService.isFeatureAvailableForScanner(
				limitedFeatures,
				this.shellQuery.clientVersion,
				this.shellQuery.packageVersion
			);
		}
		if (this.hostPlatformService.isMidc(this.shellQuery.applicationMode as ApplicationModes)) {
			const versions = this.shellQuery.scannersVersions?.map(s => s.VersionNumber);

			return this.versionService.isFeatureAvailableForAllScanners(limitedFeatures, versions);
		} else {
			return false;
		}
	}

	private isRestrictedUnitTypeAvailability(unitTypeRule: UnitTypeRuleWithSpecs, rxConfiguration: Maybe<RxConfiguration>): boolean {
		const hasSWORestriction = unitTypeRule.RestrictionUnitTypes?.SoftwareOptions
			? this.doesFeatureHaveSWO(unitTypeRule.RestrictionUnitTypes.SoftwareOptions, rxConfiguration)
			: true;

		const hasFeatureRestriction = unitTypeRule.RestrictionUnitTypes?.LimitedFeatures
			? this.isfeatureAvailable(unitTypeRule.RestrictionUnitTypes.LimitedFeatures)
			: true;

		return hasSWORestriction && hasFeatureRestriction;
	}
}
